import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { isPartialPhoneNumber, isPhoneNumber, sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useSearchHistoryShallowStore } from '@frontend/search-history';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, SearchField, useDebouncedFn, useFormField, useModalControl } from '@frontend/design-system';
import {
  AllCallsTable,
  CallRecordsFiltersType,
  usePhonePageSettingShallowStore,
} from '../../hooks/use-phone-config-state-store';
import { SetPageConfigInput } from '../voicemails/voicemails-multi.component';
import { RecentCallsTrayFilter } from './calls-filter';
import { createDefaultRecentCallsFilters, hasRecentCallsFiltersChanged } from './recent-calls-filters';

const defaultRecentCallsFilters = createDefaultRecentCallsFilters();

export const CallHistoryActionBar = () => {
  const { modalProps, triggerProps } = useModalControl();
  const { selectedLocationIds: selectedLocations } = useAppScopeStore();
  const isMultiLocationSelected = selectedLocations.length > 1;
  const { isActive: hasVoiceAi } = useVoiceAIRCustomizationFlag(selectedLocations);
  const { setDebouncedValue } = useSearchHistoryShallowStore('setDebouncedValue');
  const onSearchHistoryChange = useDebouncedFn((value) => setDebouncedValue(value), 3000);
  const [shouldShowSearch, setShouldShowSearch] = useState(false);
  const { t } = useTranslation('calls');

  const {
    config,
    setPageSize,
    setPageNumber,
    setFilters: setCallsPageFilters,
  } = usePhonePageSettingShallowStore('config', 'setPageSize', 'setPageNumber', 'setFilters');
  const pageNumber = config[AllCallsTable.RecentCalls].pageNumber;
  const pageSize = config[AllCallsTable.RecentCalls].pageSize;
  const filtersInStore = config[AllCallsTable.RecentCalls].filters;
  const filters = filtersInStore || defaultRecentCallsFilters;
  const setPageConfig = useCallback(
    (input: SetPageConfigInput) => {
      const data = typeof input === 'function' ? input({ pageSize, pageNumber }) : input;
      if (data.pageSize) setPageSize(AllCallsTable.RecentCalls, data.pageSize);
      if (data.pageNumber) setPageNumber(AllCallsTable.RecentCalls, data.pageNumber);
    },
    [pageSize, pageNumber, setPageSize, setPageNumber]
  );

  const setFilters = useCallback(
    (nextFilters: CallRecordsFiltersType) => {
      setCallsPageFilters(AllCallsTable.RecentCalls, nextFilters);
      setPageConfig({ pageNumber: 1 });
    },
    [setCallsPageFilters, setPageConfig]
  );

  const onDismissClick = useCallback(() => setShouldShowSearch(false), [setShouldShowSearch]);

  const searchHandler = useCallback(
    (searchText: string) => {
      const sanitizedNumber = sanitizePhoneNumber(searchText);
      const numberSearch =
        isPhoneNumber(sanitizedNumber) || isPartialPhoneNumber(sanitizedNumber) ? sanitizedNumber : '';
      const nameSearch = !isPhoneNumber(sanitizedNumber) && !isPartialPhoneNumber(sanitizedNumber) ? searchText : '';
      setFilters({
        ...filters,
        phoneNumber: numberSearch,
        name: nameSearch,
      });
      onSearchHistoryChange(searchText);
    },
    [filters, setFilters, onSearchHistoryChange]
  );

  const onLocationChange = useCallback(
    (locations: string[]) => {
      setFilters({
        ...filters,
        locationIds: locations,
      });
    },
    [filters, setFilters]
  );

  const searchField = useFormField(
    {
      type: 'text',
      value: filters.name,
      placeholder: t('Search name or 10-digit number'),
    },
    [filters.name]
  );
  const showSearchBadge = !!searchField.value;
  const showFilterBadge = hasRecentCallsFiltersChanged(filters, defaultRecentCallsFilters);

  const clearField = useCallback(() => {
    searchField.onChange({
      name: 'search',
      value: '',
    });
    searchHandler('');
  }, [searchField, searchHandler]);

  if (shouldShowSearch) {
    return (
      <AnimatePresence>
        <motion.div
          variants={!isMultiLocationSelected ? searchContainerVariants : {}}
          initial='hidden'
          animate='visible'
          exit='exit'
          css={[
            css`
              display: grid;
              grid-template-columns: 1fr auto;
              align-items: center;
              gap: ${theme.spacing(1)};
              position: relative;
            `,
            !isMultiLocationSelected &&
              css`
                width: 100%;
              `,
          ]}
        >
          <SearchField
            {...searchField}
            autoFocus
            css={
              !isMultiLocationSelected &&
              css`
                border-radius: ${theme.spacing(2)};
                height: 48px;
                box-shadow: ${theme.shadows.floating};
              `
            }
            containerCss={[
              !isMultiLocationSelected &&
                css`
                  & > div:focus-within {
                    box-shadow: ${theme.shadows.floating} !important;
                    border-color: ${theme.colors.neutral30} !important;
                  }
                `,
            ]}
            onChange={(e) => {
              searchField.onChange(e);

              if (!e) {
                return;
              }

              if ('target' in e) {
                searchHandler((e.target as HTMLInputElement).value);
              } else {
                searchHandler(e.value);
              }
            }}
            label=''
            name='search'
            clearable={isMultiLocationSelected}
          />
          {!isMultiLocationSelected ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                position: absolute;
                right: ${theme.spacing(3)};
              `}
            >
              {!!searchField.value && (
                <Button
                  css={css`
                    border: none;
                    padding: 0;
                    color: ${theme.colors.primary50};
                    margin-right: ${theme.spacing(1.5)};
                  `}
                  onClick={clearField}
                  variant='secondary'
                  data-testid='call-history-clear-button'
                >
                  {t('Clear')}
                </Button>
              )}
              <Button
                css={css`
                  padding: 0;
                  &:hover {
                    background: transparent;
                  }
                `}
                onClick={onDismissClick}
                variant='secondary'
                data-testid='call-history-dismiss-button'
                iconName='x'
              />
            </div>
          ) : (
            <Button variant='secondary' size='large' onClick={onDismissClick}>
              {t('Done')}
            </Button>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: selectedLocations.length > 1 ? '1fr auto auto' : 'auto auto auto 1fr',
        gridTemplateAreas: selectedLocations.length > 1 ? '"location search filter"' : '"location search filter ."',
      }}
    >
      <ScopedLocationFilterMenu
        selectedOptions={selectedLocations}
        trackingIdBase={'phone-weave2.0-voicemails'}
        onChange={onLocationChange}
        showClearAll={false}
        css={{ gridArea: 'location' }}
      />
      <Button
        variant='secondary'
        data-testid='call-history-search-button'
        iconName='search'
        size='large'
        css={[
          { gridArea: 'search' },
          css`
            ::after {
              position: absolute;
              opacity: ${showSearchBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        onClick={() => setShouldShowSearch(true)}
      />
      <Button
        variant='secondary'
        iconName='filter-alt'
        data-testid='call-history-filter-button'
        size='large'
        css={[
          { gridArea: 'filter' },
          css`
            ::after {
              position: absolute;
              opacity: ${showFilterBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        {...triggerProps}
      />
      <RecentCallsTrayFilter
        hasVoiceAi={hasVoiceAi}
        modalProps={modalProps}
        hasFilterChanged={(incomingFilters) => hasRecentCallsFiltersChanged(incomingFilters, filters)}
        filters={filters}
        isMulti={selectedLocations.length > 1}
        phoneNumberOptions={{}}
        resetFilters={() => {
          setFilters(defaultRecentCallsFilters);
        }}
        onChange={(callRecordFilters) => {
          setFilters(callRecordFilters);
        }}
      />
    </div>
  );
};

const searchContainerVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.98,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 1, 1],
    },
  },
};
